import React, { useEffect } from 'react'
import { useContentDocumentSearchQuery } from '@src/queries/ContentDocumentsSearchQuery.generated'
import { ContentSearchType } from '@src/types/graphql-types'
import { useRouter } from '@src/routes'
import { useSearchQuery } from '@src/pages/[country]/[language]/search/[searchTerm]'
import { makeStyles } from 'tss-react/mui'
import { isTermlessSearch } from '@utils/termlessSearch'
import {
  setPageLoadComplete,
  sendViewSearchResultsEvent,
} from '@utils/analytics'
import SearchResultsError from '@src/components/SearchResultsError'
import ContentSearchResultsSkeleton from './ContentSearchResultsSkeleton'
import { Container, Unstable_Grid2 as Grid } from '@mui/material'
import Pagination from '../../Pagination'
import InjectPromoProducts from '../../InjectPromoProducts'
import SearchResultsMenu from '../../SearchResultsMenu/SearchResultsMenu'
import NoResults from '../../NoResults'
import ContentItem from './ContentItem'
import { SearchFocusType } from '@src/utils/searchUtils'

const useStyles = makeStyles()((theme) => ({
  results: {
    '&> *': {
      borderBottom: `solid 1px ${theme.palette.grey[400]}`,
    },
    [theme.breakpoints.up('md')]: {
      '&> *:first-of-type': {},
      '&> *:last-of-type': {
        marginBottom: 0,
      },
    },
  },
  noMatchesMessage: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: theme.typography.fontWeightBold,
  },
  siteContentWrapper: {
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
  },
  container: {
    padding: 0,
    width: '100%',
    marginBottom: theme.spacing(5),
  },
  pagination: {
    marginBottom: theme.spacing(4),
  },
}))

interface ContentSearchResultsProps {
  searchType: SearchFocusType
}

const ContentSearchResults = ({ searchType }: ContentSearchResultsProps) => {
  const router = useRouter()
  const [query, selectedFacets, queryActions] = useSearchQuery()
  const { classes } = useStyles()
  const { data, loading, error } = useContentDocumentSearchQuery({
    variables: {
      searchTerm: isTermlessSearch(router) ? null : query.term,
      page: query.page,
      perPage: 10,
      selectedFacets,
      contentSearchType:
        query.focus === SearchFocusType.SiteContent
          ? ContentSearchType.SiteContent
          : ContentSearchType.Documents,
      ...(query.focus === SearchFocusType.SiteContent && {
        filterSite: 'filter_site:Sigmaaldrich',
      }),
    },
  })

  useEffect(() => {
    if (!loading && data) {
      setPageLoadComplete(true)

      const searchResultsCount =
        data?.getContentDocuments?.metadata?.itemCount || ''
      const searchResultStatus = data.getContentDocuments.items.length
        ? 'standard'
        : 'no results page'

      if (query.focus === searchType) {
        sendViewSearchResultsEvent({
          qFocus: query.focus || '',
          qPage: `${query.page}`,
          qType: query.type || '',
          searchResultStatus,
          searchTerm: query.term || '',
          didYouMeanTerms: undefined,
          searchResultsCount,
        })
      }
    }
  }, [loading, data, query.focus])

  const { items = [], metadata } = data?.getContentDocuments || {}
  const hasItems = items.length > 0

  if (loading) {
    return (
      <Container className={classes.container}>
        <SearchResultsMenu />
        <ContentSearchResultsSkeleton />
      </Container>
    )
  }

  if (error || !data?.getContentDocuments) {
    return (
      <Container className={classes.container}>
        <SearchResultsMenu />
        <SearchResultsError />
      </Container>
    )
  }

  if (!hasItems) {
    return <NoResults />
  }

  return (
    <Container className={classes.container}>
      <SearchResultsMenu />
      <Grid container spacing={10}>
        <Grid xs={12}>
          <div className={classes.results}>
            <InjectPromoProducts searchKey={query.term || ''} variant="dark">
              {items.map((content) => (
                <div key={content.url} className={classes.siteContentWrapper}>
                  <ContentItem
                    item={content}
                    useNextLink={query.focus === SearchFocusType.SiteContent}
                  />
                </div>
              ))}
            </InjectPromoProducts>
          </div>
          <div className={classes.pagination}>
            <Pagination
              currentPage={query.page}
              handleUpdatePage={queryActions.handlePageChange}
              numPages={metadata?.numPages || 1}
            />
          </div>
        </Grid>
      </Grid>
    </Container>
  )
}

export default ContentSearchResults
